import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { Gender } from '@enums/gender';

/**
 * Manages gender-specific settings for treatment types.
 */
export class TreatmentGenderSettings {
  public disallowed: Gender[] = [];
  public errorMessage: string = 'This treatment is not available for the selected gender.';
  public medicationOverrides: Partial<Record<Gender, Record<number, number>>> = {};
  public infoMessages: Partial<Record<Gender, string>> = {};
  public treatmentNames: Partial<Record<Gender, ConsultationTreatmentTypes>> = {};
  public excludedForUpsells: Record<string, Gender> = {
    '/consultation/be-prepared-for-the-unexpected': Gender.Male,
  };

  /**
   * Creates an instance of TreatmentGenderSettings.
   *
   * @param {Partial<TreatmentGenderSettings>} data optional data object to initialize the instance properties
   */
  public constructor(data?: Partial<TreatmentGenderSettings>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Checks if a specific gender is disallowed for this treatment setting.
   *
   * @param {Gender} gender the gender to check
   *
   * @returns {boolean} `true` if the gender is explicitly listed in `disallowed`, `false` otherwise
   */
  public isDisallowed(gender: Gender): boolean {
    return this.disallowed?.includes(gender) ?? false;
  }

  /**
   * Gets medication ID for a specific gender if gender-specific medication exists.
   *
   * @param {Gender} gender the gender to get medication for
   * @param {number} originalMedicationId the original medication ID to potentially replace
   *
   * @returns {number | null} the replacement medication ID or null if no mapping exists.
   */
  public getMedication(gender: Gender, originalMedicationId: number): number | null {
    return this.medicationOverrides[gender]?.[originalMedicationId] || null;
  }

  /**
   * Gets the info message to display for a specific gender.
   *
   * @param {Gender} gender the gender to get info message for
   *
   * @returns {string} the info message or empty string
   */
  public getInfoMessage(gender: Gender): string {
    return this.infoMessages[gender] || '';
  }

  /**
   * Gets the treatment name for a specific gender if gender-specific name exists.
   *
   * @param {Gender} gender the gender to get treatment name for
   * @param {ConsultationTreatmentTypes} baseName the base treatment name to use as fallback
   *
   * @returns {ConsultationTreatmentTypes} the gender-specific treatment name or base name
   */
  public getTreatmentName(gender: Gender, baseName: ConsultationTreatmentTypes): ConsultationTreatmentTypes {
    return this.treatmentNames[gender] || baseName;
  }
}
